import {
    Model,
    UpdateCommentParams,
    WidgetDisplayMode,
    WidgetWrapperProps,
    jUserList,
    JFileApi,
    JSocialActionApi,
    JUserApi,
    CommentWithExtensions,
} from 'jamespot-user-api';
import { jCommentList } from 'jamespot-user-api/lib/src/types/comment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';
import { DriveOrFilebank, JRCInputTinyMCERawProps } from 'types';
import { JRCAttachments } from '../JRCAttachments/JRCAttachments';
import { JRCAvatar } from '../JRCAvatar/JRCAvatar';
import { JRCButton } from '../JRCButton/JRCButton';
import { JRCDropDown } from '../JRCDropDown/JRCDropDown';
import { JRCFlexBox } from '../JRCFlex/JRCFlexBox';
import { JRCStyledHref } from '../JRCHref/JRCHref';
import { JRCHtml } from '../JRCHtml/JRCHtml';
import { JRCIcon } from '../JRCIcon/JRCIcon';
import { JRCLoader } from '../JRCLoader/JRCLoader';
import { JRCModalContent } from '../JRCModal/JRCModalContent';
import { JRCModalLayout } from '../JRCModal/JRCModalLayout';
import { JRCModal } from '../JRCModal/templates/JRCModal';
import { JRCTooltip } from '../JRCTooltip/JRCTooltip';
import { JRCTypography } from '../JRCTypography/JRCTypography';
import { JRCUserHighlightFields } from '../JRCUserHighlightFields/JRCUserHighlightFields';
import { JRCUserPopup } from '../JRCUserPopup/JRCUserPopup';
import { AvatarWrapper } from './JRCCommentsBloc';
import { JDriveApi } from 'jamespot-user-api/lib/src/apis/drive/drive';
import { JRCMessage } from '../JRCMessage/JRCMessage';
import { JQuestionAnswerApi } from 'jamespot-user-api/lib/src/apis/questionAnswer/questionAnswer';
import { useAbortController } from '../../hooks/useAbortController';
import { EditComment } from './components/EditComment';
import { JRCUserStatusIcon } from '../JRCUserStatusIcon/JRCUserStatusIcon';
import { FoldersHandlers } from '../JRCFolders/types';

export type JRCCommentProps = {
    isFocused: boolean;
    comment: CommentWithExtensions<jCommentList, ['actions']> & { pending?: boolean };
    token?: string;
    tinyMCEConfig: Pick<JRCInputTinyMCERawProps, 'commonOptions' | 'mentionsQueries' | 'tinymceScriptSrc'>;
    onCommentUpdate: (args: UpdateCommentParams) => Promise<void>;
    onCommentDelete: (idComment: number) => Promise<void>;
    deleteFile: (id: number) => Promise<void>;
    onError?: (message?: string) => void;
    highlightFields?: string[];
    userAccountStatus?: 0 | 1;
    userModel?: Model;
    isWedocActive: boolean;
    isWidgetActive: boolean;
    getAccessHash?: (fileId: number) => Promise<string>;
    onGetHashError: (message?: string) => void;
    existingWidgetsWrapperComponent: (
        comment: jCommentList,
        widgets: WidgetWrapperProps[],
        mode: WidgetDisplayMode,
        inplace: boolean,
    ) => React.ReactNode;
    activeDrives: DriveOrFilebank[];
    handlers: FoldersHandlers & {
        file: Pick<JFileApi, 'copy'>;
        drive: Pick<
            JDriveApi,
            | 'msDocuments'
            | 'dropboxDocuments'
            | 'groups'
            | 'tenants'
            | 'listAccount'
            | 'isAccountLogged'
            | 'checkAccountToken'
            | 'getAccount'
            | 'logout'
            | 'setDefaultAccount'
            | 'getDocument'
        >;
        user: Pick<JUserApi, 'get' | 'getFields'>;
        socialAction: Pick<JSocialActionApi, 'add' | 'remove' | 'getUsers'>;
        questionAnswer: Pick<JQuestionAnswerApi, 'selectResponse' | 'unselectResponse'>;
    };
    socialQuestion?: {
        isResolved: boolean;
        responseId?: number;
        onSelectResponse: (responseId: number) => void;
        onUnselectResponse: () => void;
        canSelectResponse: boolean;
    };
    articleId: number;
    onReport: (commentUri: string) => void;
};

const MainContainer = styled.div`
    padding: 4px 12px;
`;

const CommentContainer = styled.div<{ focused: boolean; pending?: boolean }>`
    display: flex;
    gap: 14px;
    padding: 10px 10px 0 10px;
    border-radius: 8px;
    background-color: ${({ theme }) => theme.color.white};
    border: 2px solid ${({ focused, theme }) => (focused ? theme.color.primary : theme.color.white)};

    &:first-child {
        margin-top: 0;
    }

    &:last-child {
        margin-bottom: 0;
    }

    animation: ${({ pending }) => (pending ? `pendingAnimation 1.5s infinite` : 'none')};

    @keyframes pendingAnimation {
        0%, 100% {
            opacity: 0.3;
        }
        50% {
            opacity: 0.7;
        }
    }
}
`;

const CommentHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const UserDisplay = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const CommentWrapper = styled.div`
    width: 100%;
`;

const CommentActionsWrapper = styled.div`
    margin-left: auto;
    margin-top: -1px;
    margin-right: 2px;
    height: fit-content;

    &:hover {
        cursor: pointer;
    }
`;

export const DropdownButton = styled.button`
    display: block;
    width: 100%;
    margin: 4px 0;
    padding: 4px 20px;

    &:hover {
        color: ${({ theme }) => theme.color.primary};
    }
`;

const IconWrapper = styled.span`
    &:hover {
        cursor: pointer;
    }
`;

const AttachmentsWrapper = styled.div`
    width: 100%;
    margin-top: 10px;
    padding-top: 12px;
    border-top: 1px solid ${({ theme }) => theme.color.grey1};
`;

const CommentContentWrapper = styled.div<{ isDeployed: boolean }>`
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    ${({ isDeployed }) =>
        isDeployed
            ? ''
            : `
        overflow: hidden;
    `}
`;
const CommentContentWrapperWidget = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const DeployButton = styled.button`
    border: none;
    background: none;
    color: ${({ theme }) => theme.color.primary};
    margin: 8px 0;
    transition: color 100ms;

    &:hover {
        color: ${({ theme }) => theme.color.primaryL15};
    }
`;

const DeployGradient = styled.div`
    background: linear-gradient(0deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 60%, rgba(255, 255, 255, 0) 100%);
    margin-top: -30px;
    position: relative;
    padding-top: 30px;
`;

const SocialActionsWrapper = styled.div`
    border-top: 1px solid ${({ theme }) => theme.color.grey1};
    display: flex;
    gap: 12px;
    align-items: center;
    height: 38px;
    margin-top: 12px;
`;

const SocialActionWrapper = styled.button`
    display: flex;
    gap: 3px;
    align-items: center;

    &:hover p,
    &:hover i::before {
        color: ${({ theme }) => theme.color.primary};
        cursor: pointer;
    }
`;

const SocialActionSub = styled.div`
    display: flex;
    gap: 6px;
    align-items: center;
`;

const SocialActionCount = styled.span`
    display: inline-flex;
    justify-content: center;
    min-height: 19px;
    min-width: 19px;
    line-height: 19px;
    background-color: ${({ theme }) => theme.color.primary};
    color: ${({ theme }) => theme.color.white};
    border-radius: 100px;
    transition: background-color 100ms;

    &:hover {
        background-color: ${({ theme }) => theme.color.primaryL15};
        cursor: pointer;
    }
`;

const UserEntry = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 24px;
`;

const AnswerWrapper = styled.div`
    margin-top: ${({ theme }) => theme.space.sm}px;
`;

export const JRCComment = ({
    isFocused,
    comment,
    token,
    tinyMCEConfig,
    onCommentUpdate,
    onCommentDelete,
    onError,
    highlightFields,
    userModel,
    userAccountStatus,
    isWedocActive,
    isWidgetActive,
    getAccessHash,
    onGetHashError,
    deleteFile,
    existingWidgetsWrapperComponent,
    activeDrives,
    handlers,
    socialQuestion,
    articleId,
    onReport,
}: JRCCommentProps) => {
    const theme = useTheme();
    const intl = useIntl();
    const [formattedDate, setFormattedDate] = useState<string>();
    const [commentHover, setCommentHover] = useState(false);
    const [actionsHover, setActionsHover] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isEditMode, setEditMode] = useState(false);
    const [commentContent, setCommentContent] = useState(comment.description);
    const [loading, setLoading] = useState(false);
    const [isDeployed, setDeployed] = useState(false);
    const [isCropNeeded, setCropNeeded] = useState(true);

    const [isSocialActionUserListModalOpen, setSocialActionUserListModalOpen] = useState(false);
    const [socialActionUsers, setSocialActionUsers] = useState<jUserList[]>();
    const [socialActionLabel, setSocialActionLabel] = useState<string>();
    const contentRef = useRef<HTMLDivElement>(null);
    const contentWrapperRef = useRef<HTMLDivElement>(null);

    const abortController = useAbortController();

    const formatCommentDate = useCallback(
        (d: string) => {
            const date = new Date(d);
            const now = new Date();
            const diff = now.getTime() - date.getTime();
            if (diff < 1000 * 60) {
                return intl.formatMessage({ id: 'COMMENT_Just_Now' });
            } else if (diff < 1000 * 60 * 60) {
                return intl.formatMessage({ id: 'COMMENT_Minutes_Ago' }, { minutes: Math.round(diff / (1000 * 60)) });
            } else if (diff < 1000 * 60 * 60 * 24 && date.getDate() === now.getDate()) {
                return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
            } else if (diff < 1000 * 60 * 60 * 24 * 31 && date.getMonth() === now.getMonth()) {
                return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
            } else {
                return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(
                    2,
                    '0',
                )}/${date.getFullYear()}`;
            }
        },
        [intl],
    );

    const updateComment = useCallback(() => {
        if (token && commentContent) {
            setLoading(true);
            onCommentUpdate({ idComment: comment.id, description: commentContent, token }).then(() => {
                setEditMode(false);
                setLoading(false);
            });
        }
    }, [token, comment, commentContent, onCommentUpdate]);

    const deleteComment = useCallback(() => {
        setLoading(true);
        onCommentDelete(comment.id)
            .then(() => {
                setDeleteModalOpen(false);
                setLoading(false);
                if (socialQuestion && socialQuestion.responseId === comment.id) {
                    socialQuestion.onUnselectResponse();
                }
            })
            .catch((e) => {
                if (onError) {
                    onError(e.errorMsg);
                }
            });
    }, [comment.id, onCommentDelete, onError, socialQuestion]);

    const openSocialActionUserListModal = (type: string, label: string) => {
        setSocialActionLabel(label);
        setSocialActionUserListModalOpen(true);
        handlers.socialAction
            .getUsers({
                targetId: comment.id,
                targetType: comment.type,
                type,
            })
            .then((res) => {
                setSocialActionUsers(res.result);
            })
            .catch((e) => {
                if (onError) {
                    onError(e.errorMsg);
                }
            });
    };

    const closeSocialActionUserListModal = () => {
        setSocialActionUserListModalOpen(false);
        setSocialActionUsers(undefined);
        setSocialActionLabel(undefined);
    };

    useEffect(() => {
        setFormattedDate(formatCommentDate(comment.dateCreation));
        const interval = setInterval(() => {
            setFormattedDate(formatCommentDate(comment.dateCreation));
        }, 1000 * 60);
        return () => clearInterval(interval);
    }, [comment, formatCommentDate]);

    useEffect(() => {
        setCropNeeded(
            contentWrapperRef.current !== null &&
                contentRef.current !== null &&
                contentRef.current.clientHeight > contentWrapperRef.current.clientHeight,
        );

        const observer = new ResizeObserver(() => {
            setCropNeeded(
                contentWrapperRef.current !== null &&
                    contentRef.current !== null &&
                    contentRef.current.clientHeight > contentWrapperRef.current.clientHeight,
            );
        });

        if (contentRef.current) {
            observer.observe(contentRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [comment]);

    const _toggleAction = (type: string, currentActive?: boolean) => {
        if (currentActive) {
            handlers.socialAction
                .remove({
                    targetId: comment.id,
                    targetType: comment.type,
                    type,
                })
                .catch((e) => {
                    if (onError) {
                        onError(e.errorMsg);
                    }
                });
        } else {
            handlers.socialAction.add({ targetId: comment.id, targetType: comment.type, type }).catch((e) => {
                if (onError) {
                    onError(e.errorMsg);
                }
            });
        }
    };

    const onDeleteAttachment = useCallback(
        (id: number) => {
            return new Promise<void>((resolve, reject) => {
                if (token) {
                    deleteFile(id)
                        .then(() => {
                            updateComment();
                            resolve();
                        })
                        .catch((e) => {
                            if (onError) {
                                onError(e.errorMsg);
                            }
                            reject();
                        });
                }
            });
        },
        [deleteFile, onError, token, updateComment],
    );

    const widgetWrapperComponent = useMemo(() => {
        if (!comment._widgets) return;

        return existingWidgetsWrapperComponent(comment, comment._widgets, 'view', true);
    }, [comment, existingWidgetsWrapperComponent]);

    const selectResponse = useCallback(() => {
        if (!socialQuestion) return;
        handlers.questionAnswer
            .selectResponse({ idResponse: comment.id, idQuestion: articleId }, { signal: abortController.signal })
            .then(() => {
                socialQuestion.onSelectResponse(comment.id);
            })
            .catch((e) => {
                if (onError && !abortController.signal.aborted) {
                    onError(e.errorMsg);
                }
            });
    }, [abortController.signal, articleId, comment.id, handlers.questionAnswer, onError, socialQuestion]);

    const unselectAnswer = useCallback(() => {
        if (!socialQuestion) return;
        handlers.questionAnswer
            .unselectResponse({ idQuestion: articleId }, { signal: abortController.signal })
            .then(() => {
                socialQuestion.onUnselectResponse();
            })
            .catch((e) => {
                if (onError && !abortController.signal.aborted) {
                    onError(e.errorMsg);
                }
            });
    }, [abortController.signal, articleId, handlers.questionAnswer, onError, socialQuestion]);

    return (
        <MainContainer data-cy={`comment-${comment.id}`}>
            <CommentContainer
                pending={comment.pending}
                focused={isFocused}
                onMouseEnter={() => setCommentHover(true)}
                onMouseLeave={() => setCommentHover(false)}>
                <AvatarWrapper>
                    <JRCStyledHref
                        href={comment._user._url}
                        aria-label={intl.formatMessage(
                            { id: 'GLOBAL_Redirect_To_User_Profile_React' },
                            { username: comment._user.title },
                        )}>
                        <JRCAvatar
                            uri={comment._user.uri}
                            size={5}
                            variant="circle"
                            timestamp={comment._user.dateModified}
                        />
                    </JRCStyledHref>
                </AvatarWrapper>
                <CommentWrapper>
                    {isEditMode && token ? (
                        <EditComment
                            comment={comment}
                            loading={loading}
                            token={token}
                            activeDrives={activeDrives}
                            commentContent={commentContent}
                            setCommentContent={setCommentContent}
                            tinyMCEConfig={tinyMCEConfig}
                            updateComment={updateComment}
                            handlers={handlers}
                            closeEditMode={() => setEditMode(false)}
                            deleteFile={deleteFile}
                            onError={onError}
                        />
                    ) : (
                        <CommentWrapper>
                            <JRCFlexBox x={'space-between'}>
                                <CommentHeader>
                                    <JRCUserPopup
                                        uri={comment._user.uri}
                                        onError={onError}
                                        handlers={{ user: handlers.user }}>
                                        <UserDisplay>
                                            <JRCStyledHref href={comment._user._url}>
                                                {comment._user.title}
                                            </JRCStyledHref>
                                            <JRCUserStatusIcon
                                                user={comment._user}
                                                isUserActive={userAccountStatus === 1}
                                            />
                                        </UserDisplay>
                                    </JRCUserPopup>
                                    <JRCTypography size={'s'} color={'grey4'} aria-hidden>
                                        •
                                    </JRCTypography>
                                    <JRCTooltip
                                        position="right"
                                        description={intl.formatDate(comment.dateCreation, {
                                            year: '2-digit',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })}>
                                        <JRCTypography color={'grey4'}>{formattedDate}</JRCTypography>
                                    </JRCTooltip>
                                </CommentHeader>
                                {(comment._right.update ||
                                    comment._right.delete ||
                                    socialQuestion?.canSelectResponse) && (
                                    <>
                                        <CommentActionsWrapper
                                            onMouseEnter={() => setActionsHover(true)}
                                            onMouseLeave={() => setActionsHover(false)}>
                                            <JRCDropDown
                                                withBorder
                                                onToggle={(isOpen) => setIsDropdownOpen(isOpen)}
                                                trigger={
                                                    <IconWrapper>
                                                        <JRCIcon
                                                            name={'icon-cogwheel'}
                                                            size={12}
                                                            color={
                                                                commentHover || isDropdownOpen
                                                                    ? actionsHover || isDropdownOpen
                                                                        ? theme.color.grey4
                                                                        : theme.color.grey3
                                                                    : theme.color.white
                                                            }
                                                        />
                                                    </IconWrapper>
                                                }
                                                dropdown={(collapseDropdown) => (
                                                    <>
                                                        {comment._right.update && (
                                                            <DropdownButton
                                                                onClick={() => {
                                                                    collapseDropdown();
                                                                    setEditMode(true);
                                                                }}>
                                                                {intl.formatMessage({ id: 'GLOBAL_Edit' })}
                                                            </DropdownButton>
                                                        )}
                                                        {comment._right.delete && (
                                                            <DropdownButton
                                                                onClick={() => {
                                                                    collapseDropdown();
                                                                    setDeleteModalOpen(true);
                                                                }}>
                                                                {intl.formatMessage({ id: 'GLOBAL_Delete' })}
                                                            </DropdownButton>
                                                        )}
                                                        {socialQuestion?.responseId === comment.id &&
                                                            socialQuestion?.canSelectResponse && (
                                                                <DropdownButton
                                                                    onClick={() => {
                                                                        collapseDropdown();
                                                                        unselectAnswer();
                                                                    }}>
                                                                    {intl.formatMessage({
                                                                        id: 'COMMENT_Unselect_Response',
                                                                    })}
                                                                </DropdownButton>
                                                            )}
                                                    </>
                                                )}
                                                align="right"
                                            />
                                        </CommentActionsWrapper>
                                        <JRCModal
                                            buttons={[
                                                {
                                                    color: 'cancel',
                                                    clickHandler: () => setDeleteModalOpen(false),
                                                    children: <FormattedMessage id={'GLOBAL_Cancel'} />,
                                                },
                                                {
                                                    color: 'danger',
                                                    clickHandler: deleteComment,
                                                    children: <FormattedMessage id={'GLOBAL_Delete'} />,
                                                    loading,
                                                },
                                            ]}
                                            open={isDeleteModalOpen}
                                            closeHandler={() => setDeleteModalOpen(false)}
                                            title={intl.formatMessage({ id: 'GLOBAL_Delete' })}>
                                            {intl.formatMessage({ id: 'COMMENT_Delete_Comment' })}
                                        </JRCModal>
                                    </>
                                )}
                            </JRCFlexBox>
                            {highlightFields && userModel && (
                                <JRCUserHighlightFields
                                    user={comment._user}
                                    highlightFields={highlightFields}
                                    userModel={userModel}
                                />
                            )}
                            {socialQuestion && (
                                <>
                                    {socialQuestion.responseId === comment.id && (
                                        <AnswerWrapper>
                                            <JRCMessage
                                                variant={'success'}
                                                message={intl.formatMessage({ id: 'COMMENT_Chosen_Response' })}
                                            />
                                        </AnswerWrapper>
                                    )}
                                    {!socialQuestion.isResolved && socialQuestion.canSelectResponse && (
                                        <AnswerWrapper>
                                            <JRCButton
                                                onClick={selectResponse}
                                                size={'s'}
                                                label={intl.formatMessage({ id: 'COMMENT_Choose_Response' })}
                                                dataCy={'questionAnswers-select-response-button'}
                                            />
                                        </AnswerWrapper>
                                    )}
                                </>
                            )}
                            <CommentContentWrapper ref={contentWrapperRef} isDeployed={isDeployed}>
                                <JRCHtml ref={contentRef} __html={comment.description ?? undefined} />
                                {isWidgetActive && (
                                    <CommentContentWrapperWidget>
                                        {comment._widgets && widgetWrapperComponent}
                                    </CommentContentWrapperWidget>
                                )}
                            </CommentContentWrapper>
                            {isCropNeeded &&
                                (isDeployed ? (
                                    <DeployButton onClick={() => setDeployed(false)}>
                                        {intl.formatMessage({ id: 'GLOBAL_See_Less' })}
                                    </DeployButton>
                                ) : (
                                    <DeployGradient>
                                        <DeployButton onClick={() => setDeployed(true)}>
                                            {intl.formatMessage({ id: 'GLOBAL_See_More' })}
                                        </DeployButton>
                                    </DeployGradient>
                                ))}
                            {comment._attachedFiles && comment._attachedFiles.length > 0 && (
                                <AttachmentsWrapper>
                                    <JRCAttachments
                                        files={comment._attachedFiles}
                                        onError={onGetHashError}
                                        isWedocActive={isWedocActive}
                                        getAccessHash={getAccessHash}
                                        onDelete={comment._right.update && token ? onDeleteAttachment : undefined}
                                    />
                                </AttachmentsWrapper>
                            )}
                            {comment._extend.actions && comment._extend.actions.length > 0 && (
                                <SocialActionsWrapper>
                                    {comment._extend.actions.map((socialAction, i) => (
                                        <SocialActionSub key={i}>
                                            <SocialActionWrapper
                                                data-cy={`social-action-${socialAction.type}`}
                                                className={
                                                    socialAction.type === 'reportaction'
                                                        ? 'contentReportButton'
                                                        : undefined
                                                }
                                                onClick={() => {
                                                    if (socialAction.type === 'reportaction') {
                                                        onReport(comment.uri);
                                                    } else {
                                                        _toggleAction(socialAction.type, socialAction.isActive);
                                                    }
                                                }}>
                                                <JRCIcon
                                                    size={14}
                                                    color={socialAction.isActive ? 'primary' : 'grey4'}
                                                    name={
                                                        'icon-' +
                                                        socialAction.icon +
                                                        (socialAction.isActive ? '-on' : '')
                                                    }
                                                />
                                                <JRCTypography
                                                    size={'s'}
                                                    color={socialAction.isActive ? 'primary' : 'grey4'}>
                                                    {socialAction.label}
                                                </JRCTypography>
                                            </SocialActionWrapper>
                                            {socialAction.count !== undefined && socialAction.count > 0 && (
                                                <JRCTypography size={'s'}>
                                                    <SocialActionCount
                                                        data-cy={`social-action-count-${socialAction.type}`}
                                                        onClick={() =>
                                                            openSocialActionUserListModal(
                                                                socialAction.type,
                                                                socialAction.label,
                                                            )
                                                        }>
                                                        {socialAction.count}
                                                    </SocialActionCount>
                                                </JRCTypography>
                                            )}
                                        </SocialActionSub>
                                    ))}
                                </SocialActionsWrapper>
                            )}
                        </CommentWrapper>
                    )}
                </CommentWrapper>
            </CommentContainer>
            <JRCModalLayout
                variant={'fixed-height'}
                maxHeight={'800px'}
                open={isSocialActionUserListModalOpen}
                closeHandler={closeSocialActionUserListModal}>
                <JRCModalContent
                    dataCy="social-action-modal"
                    overflow={'scroll'}
                    onClickIconClose={closeSocialActionUserListModal}
                    title={socialActionLabel + (socialActionUsers ? ` (${socialActionUsers.length})` : '')}>
                    {socialActionUsers ? (
                        <UserEntry>
                            {socialActionUsers.map((user, i) => (
                                <JRCFlexBox key={`visitor-${i}`} y={'center'} gap={24}>
                                    <JRCAvatar size={5} uri={user.uri} />
                                    <JRCStyledHref href={user._url}>
                                        <JRCTypography>{user.title}</JRCTypography>
                                    </JRCStyledHref>
                                </JRCFlexBox>
                            ))}
                        </UserEntry>
                    ) : (
                        <JRCLoader size={'l'} />
                    )}
                </JRCModalContent>
            </JRCModalLayout>
        </MainContainer>
    );
};
